// Admin → Webhook Endpoints: every outbound webhook endpoint. Client-backed: one request loads the
// complete set (there are only ever a handful of endpoints).

import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { MeTable } from '@CommonComponents'
import { Clipped } from '../../components/me_table/cells/InteractiveCells'
import { client } from '@Utils'
import { navigate } from './navigate'
import { Missing, StatusBadge } from './StatusBadge'

const courseList = row => row.courses.map(course => course.coursenum).join(', ')

const CoursesCell = ({ row }) =>
  row.courses.length ? (
    <Clipped text={courseList(row)}>
      <span>
        {row.courses.map((course, i) => (
          <React.Fragment key={course.coursenum}>
            {i > 0 && ' '}
            <span className="me-badge-tint me-badge-tint-neutral">
              {course.coursenum}
            </span>
          </React.Fragment>
        ))}
      </span>
    </Clipped>
  ) : (
    <Missing />
  )

CoursesCell.propTypes = { row: PropTypes.object.isRequired }

export const tableConfig = {
  tableId: 'admin-webhook-endpoints',
  title: 'Endpoints',
  caption: 'Endpoints',
  rowKey: 'id',
  columns: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      sortable: true,
      searchable: true,
      required: true,
    },
    {
      key: 'provisioned_for',
      label: 'Provisioned for',
      type: 'text',
      sortable: true,
      searchable: true,
    },
    {
      key: 'courses',
      label: 'Courses',
      type: 'text',
      sortable: true,
      searchable: true,
      wrap: 'ellipsis',
      value: courseList,
      searchText: courseList,
      render: row => <CoursesCell row={row} />,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'badge',
      sortable: true,
      searchable: true,
      filterable: true,
      value: row => row.status.label,
      searchText: row => row.status.label,
      render: row => <StatusBadge status={row.status} />,
    },
    {
      key: 'failures',
      label: 'Failures',
      type: 'numeric',
      sortable: true,
    },
    {
      key: 'last_success',
      label: 'Last success (UTC)',
      type: 'text',
      sortable: true,
      wrap: 'nowrap',
      value: row => row.last_success,
      render: row => row.last_success || <Missing />,
    },
  ],
  defaultSort: { key: 'name', direction: 'ascending' },
  pageSizes: [25, 50],
  defaultPageSize: 25,
  source: { mode: 'client', max: 500 },
  searchLabel: 'Search endpoints',
  searchPlaceholder: 'Search name, course, or partner email',
  emptyState: {
    title: 'No endpoints',
    description: 'No webhook endpoints have been provisioned yet.',
  },
}

export const rowActions = row => [
  {
    key: 'edit',
    label: 'Edit',
    icon: 'fa-pen-to-square',
    onSelect: () => navigate(row.actions.edit_url),
  },
  {
    key: 'deliveries',
    label: 'Deliveries',
    icon: 'fa-list-check',
    onSelect: () => navigate(row.actions.deliveries_url),
  },
]

export const EndpointDetail = ({ row }) => (
  <div>
    <h4 className="me-ssh">URL</h4>
    <p className="small mb-2" style={{ wordBreak: 'break-all' }}>
      {row.url}
    </p>
    <h4 className="me-ssh">Event types</h4>
    <p className="mb-2">
      {row.event_types.length ? (
        row.event_types.map(eventType => (
          <span
            key={eventType}
            className="me-badge-tint me-badge-tint-neutral mr-1"
          >
            {eventType}
          </span>
        ))
      ) : (
        <Missing />
      )}
    </p>
    <h4 className="me-ssh">Courses</h4>
    {row.courses.map(course => (
      <p key={course.coursenum} className="small mb-1">
        {course.coursenum} — {course.title}
        {!course.active && ` (suspended: ${course.suspended_reason})`}
      </p>
    ))}
  </div>
)

EndpointDetail.propTypes = { row: PropTypes.object.isRequired }

export const EndpointsTable = ({ dataUrl }) => {
  const [status, setStatus] = useState('loading')
  const [rows, setRows] = useState([])

  const load = useCallback(() => {
    setStatus('loading')
    client
      .get(dataUrl)
      .then(response => {
        setRows(response.data.rows)
        setStatus('ready')
      })
      .catch(() => setStatus('error'))
  }, [dataUrl])

  useEffect(() => load(), [load])

  return (
    <MeTable
      config={tableConfig}
      rows={rows}
      status={status}
      onRetry={load}
      rowActions={rowActions}
      renderDetail={row => <EndpointDetail row={row} />}
    />
  )
}

EndpointsTable.propTypes = { dataUrl: PropTypes.string.isRequired }
